import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Sparkles, Send, RotateCcw, User } from "lucide-react";

const SUGGESTIONS = [
  "Quel est mon CA du mois et ma marge ?",
  "Quelles sont mes 3 priorités urgentes ?",
  "Quels clients me rapportent le plus ?",
  "Quel produit se vend le moins ce mois-ci ?",
  "Combien j'ai d'impayés à relancer ?",
  "Comment je peux augmenter mes ventes B2B ?",
];

const AssistantIA = () => {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(() => localStorage.getItem("ai_session_id") || null);
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  useEffect(() => {
    if (!sessionId) return;
    axios.get(`${API}/ai/messages?session_id=${sessionId}`, { headers })
      .then(r => setMessages(r.data || []))
      .catch(() => {});
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const send = async (text) => {
    const question = (text ?? input).trim();
    if (!question || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: question, created_at: new Date().toISOString() }]);
    setLoading(true);
    try {
      const res = await axios.post(`${API}/ai/chat`, { message: question, session_id: sessionId }, { headers, timeout: 60000 });
      const { session_id: sid, response } = res.data;
      if (!sessionId) { setSessionId(sid); localStorage.setItem("ai_session_id", sid); }
      setMessages((m) => [...m, { role: "assistant", content: response, created_at: new Date().toISOString() }]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur IA");
      setMessages((m) => m.slice(0, -1));
    } finally { setLoading(false); }
  };

  const newSession = () => {
    setSessionId(null);
    localStorage.removeItem("ai_session_id");
    setMessages([]);
    toast.success("Nouvelle conversation");
  };

  const renderMd = (text) => {
    // Rendu markdown minimal (titres ## , listes -, gras **)
    const lines = (text || "").split("\n");
    return lines.map((line, i) => {
      if (line.startsWith("## ")) return <h3 key={i} className="font-bold text-slate-800 mt-3 mb-1">{line.slice(3)}</h3>;
      if (line.startsWith("- ")) return <div key={i} className="ml-2 text-slate-700">• {renderInline(line.slice(2))}</div>;
      if (line.startsWith("---")) return <hr key={i} className="my-2 border-slate-200" />;
      if (!line.trim()) return <div key={i} className="h-2" />;
      return <div key={i} className="text-slate-700">{renderInline(line)}</div>;
    });
  };
  const renderInline = (t) => {
    const parts = t.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((p, i) => p.startsWith("**") && p.endsWith("**")
      ? <b key={i} className="text-slate-900">{p.slice(2, -2)}</b>
      : <span key={i}>{p}</span>);
  };

  return (
    <div className="space-y-6" data-testid="assistant-ia-page">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-800 flex items-center gap-2">
            <Sparkles className="w-7 h-7 text-indigo-600" /> Assistant IA — Chef d'entreprise
          </h1>
          <p className="text-slate-500 mt-1">Pose n'importe quelle question sur ton business — l'IA a accès à tes données en temps réel.</p>
        </div>
        <Button variant="outline" onClick={newSession} data-testid="new-conversation-btn">
          <RotateCcw className="w-4 h-4 mr-2" />Nouvelle conversation
        </Button>
      </div>

      {messages.length === 0 && (
        <Card className="border-indigo-200 bg-indigo-50/40">
          <CardContent className="py-6">
            <p className="text-sm text-slate-600 mb-3">💡 Suggestions pour démarrer :</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {SUGGESTIONS.map((s, i) => (
                <button
                  key={i}
                  onClick={() => send(s)}
                  className="text-left text-sm px-3 py-2 rounded-lg bg-white border border-indigo-100 hover:border-indigo-300 hover:bg-indigo-50 transition text-slate-700"
                  data-testid={`suggestion-${i}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-0">
          <div className="h-[55vh] overflow-y-auto p-5 space-y-4" data-testid="chat-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex gap-3 ${m.role === "user" ? "justify-end" : ""}`}>
                {m.role === "assistant" && (
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shrink-0">
                    <Sparkles className="w-4 h-4 text-white" />
                  </div>
                )}
                <div className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm ${m.role === "user" ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-800"}`}>
                  {m.role === "assistant" ? renderMd(m.content) : m.content}
                </div>
                {m.role === "user" && (
                  <div className="w-8 h-8 rounded-full bg-slate-300 flex items-center justify-center shrink-0">
                    <User className="w-4 h-4 text-slate-700" />
                  </div>
                )}
              </div>
            ))}
            {loading && (
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shrink-0">
                  <Sparkles className="w-4 h-4 text-white animate-pulse" />
                </div>
                <div className="bg-slate-100 rounded-2xl px-4 py-3 text-sm text-slate-500 italic">L'IA réfléchit…</div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>
          <div className="border-t p-3 flex gap-2">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), send())}
              placeholder="Pose ta question…"
              disabled={loading}
              className="flex-1"
              data-testid="ai-input"
            />
            <Button onClick={() => send()} disabled={loading || !input.trim()} className="bg-indigo-600 hover:bg-indigo-700" data-testid="ai-send-btn">
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AssistantIA;
